import React, { useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    Clock,
    ExternalLink,
    Printer,
    RefreshCw,
    Search,
    Image as ImageIcon,
    Thermometer,
} from "lucide-react";
import Page from "../../components/Page";

async function apiGetJSON(url) {
    const r = await fetch(url, { credentials: "include" });
    const j = await r.json().catch(() => null);
    if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
    const ok = !!(j && (j.success === true || j.ok === true));
    if (!ok) throw new Error(j?.error || "Risposta non valida");
    return j;
}

async function apiPostJSON(url, body) {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
    const ok = !!(j && (j.success === true || j.ok === true));
    if (!ok) throw new Error(j?.error || "Risposta non valida");
    return j;
}

function fmtDT(v) {
    if (!v) return "—";
    const d = new Date(String(v).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString("it-IT");
}

function fmtDate(v) {
    if (!v) return "—";
    return String(v);
}

function clsBadge(stato, isOverdue) {
    if (stato === "completato") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (isOverdue) return "bg-red-50 text-red-700 border-red-200";
    return "bg-amber-50 text-amber-800 border-amber-200";
}

function sourceModeLabel(v) {
    if (v === "ddt") return "Origine: DDT";
    if (v === "photos") return "Origine: Foto";
    if (v === "both") return "Origine: DDT + Foto";
    return v || "";
}

function snapshotAvg(snapshot) {
    const v = Number(snapshot?.avg);
    return Number.isFinite(v) ? v : "";
}

function SnapshotMini({ snapshot }) {
    if (!snapshot) return null;
    const points = [snapshot.point_1, snapshot.point_2, snapshot.point_3];
    return (
        <div className="mt-2 flex flex-wrap gap-2">
            {points.map((value, idx) => {
                const n = value === null || value === undefined || value === "" ? null : Number(value);
                return (
                    <span key={idx} className="rounded-xl border border-cyan-100 bg-cyan-50 px-2 py-1 text-xs text-cyan-800">
                        P{idx + 1}: {n === null || Number.isNaN(n) ? "--" : `${n.toFixed(1)} C`}
                    </span>
                );
            })}
        </div>
    );
}

export default function RegistroAbbattimenti() {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [items, setItems] = useState([]);
    const [completingId, setCompletingId] = useState(null);
    const [tempFineById, setTempFineById] = useState({});
    const [cryoEndById, setCryoEndById] = useState({});
    const [cryoLiveById, setCryoLiveById] = useState({});
    const [cryoTrendById, setCryoTrendById] = useState({});
    const [capturingId, setCapturingId] = useState(null);
    const [savingId, setSavingId] = useState(null);

    const [q, setQ] = useState("");
    const [onlyInCorso, setOnlyInCorso] = useState(false);
    const [onlyScaduti, setOnlyScaduti] = useState(false);
    const [onlyConFoto, setOnlyConFoto] = useState(false);

    async function load() {
        setLoading(true);
        setErr("");
        try {
            const j = await apiGetJSON("/api/abbattimenti_list.php");
            const arr = Array.isArray(j.items) ? j.items : [];
            setItems(arr);
        } catch (e) {
            setErr(String(e?.message || e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    async function handleComplete(it) {
        const rawValue = tempFineById[it.id];
        const tempFine = Number(rawValue);

        if (rawValue === undefined || rawValue === "" || Number.isNaN(tempFine)) {
            window.alert("Inserisci una temperatura finale valida.");
            return;
        }

        try {
            setSavingId(it.id);
            await apiPostJSON("/api/abbattimenti_save.php", {
                id: Number(it.id),
                mode: "complete",
                temp_fine: tempFine,
                cryo_snapshot_end: cryoEndById[it.id] || null,
                cryo_ended_at: cryoEndById[it.id]?.captured_at || null,
            });
            setCompletingId(null);
            setTempFineById((prev) => ({ ...prev, [it.id]: "" }));
            setCryoEndById((prev) => ({ ...prev, [it.id]: null }));
            await load();
        } catch (e) {
            window.alert(String(e?.message || e));
        } finally {
            setSavingId(null);
        }
    }

    async function captureCryoEnd(it) {
        if (!it.cryo_device_id) return;
        try {
            setCapturingId(it.id);
            const j = await apiGetJSON(`/api/cryo_abbattimenti_snapshot.php?device_id=${encodeURIComponent(it.cryo_device_id)}`);
            const snapshot = j.snapshot || null;
            const avg = snapshotAvg(snapshot);
            setCryoEndById((prev) => ({ ...prev, [it.id]: snapshot }));
            if (avg !== "") {
                setTempFineById((prev) => ({ ...prev, [it.id]: String(avg) }));
            }
        } catch (e) {
            window.alert(`Lettura Cryo non disponibile: ${String(e?.message || e)}`);
        } finally {
            setCapturingId(null);
        }
    }

    async function captureCryoLive(it) {
        if (!it.cryo_device_id) return;
        try {
            setCapturingId(it.id);
            const from = encodeURIComponent(it.inizio || it.cryo_started_at || "");
            const j = await apiGetJSON(`/api/cryo_abbattimenti_history.php?device_id=${encodeURIComponent(it.cryo_device_id)}&from=${from}`);
            setCryoLiveById((prev) => ({ ...prev, [it.id]: j.current || null }));
            setCryoTrendById((prev) => ({ ...prev, [it.id]: Array.isArray(j.items) ? j.items : [] }));
        } catch (e) {
            window.alert(`Lettura Cryo non disponibile: ${String(e?.message || e)}`);
        } finally {
            setCapturingId(null);
        }
    }

    function CryoTrendCard({ snapshot, rows }) {
        const points = Array.isArray(rows) ? rows.filter((r) => r.avg_temp !== null && r.avg_temp !== undefined) : [];
        const width = 320;
        const height = 96;
        const vals = points.map((r) => Number(r.avg_temp)).filter((v) => Number.isFinite(v));
        const min = vals.length ? Math.min(...vals) : 0;
        const max = vals.length ? Math.max(...vals) : 1;
        const span = Math.max(1, max - min);
        const poly = vals
            .map((v, idx) => {
                const x = vals.length <= 1 ? width / 2 : (idx / (vals.length - 1)) * width;
                const y = height - ((v - min) / span) * (height - 18) - 9;
                return `${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(" ");

        return (
            <div className="mt-3 rounded-2xl border border-cyan-100 bg-cyan-50/70 px-4 py-3 text-sm text-cyan-900">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <div className="font-semibold">Cryo a cuore live</div>
                        <div className="text-xs text-cyan-700">3 sonde: punto 1, punto 2, punto 3</div>
                    </div>
                    <div className="text-right text-xs text-cyan-700">
                        {snapshot?.last_seen ? `Ultima: ${fmtDT(snapshot.last_seen)}` : "Ultima: --"}
                    </div>
                </div>
                <SnapshotMini snapshot={snapshot} />
                <div className="mt-3 rounded-xl bg-white p-2">
                    {vals.length > 1 ? (
                        <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full">
                            <polyline fill="none" stroke="#0e7490" strokeWidth="3" points={poly} />
                        </svg>
                    ) : (
                        <div className="grid h-24 place-items-center text-xs text-slate-400">
                            Storico non ancora sufficiente
                        </div>
                    )}
                    <div className="flex justify-between text-[11px] text-slate-500">
                        <span>{points[0]?.rilevata_il ? fmtDT(points[0].rilevata_il) : "inizio"}</span>
                        <span>{vals.length ? `${vals[vals.length - 1].toFixed(1)} C media` : "--"}</span>
                    </div>
                </div>
            </div>
        );
    }

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();

        return (items || []).filter((it) => {
            if (onlyInCorso && it.stato !== "in_corso") return false;
            if (onlyScaduti && !it.is_overdue) return false;
            if (onlyConFoto && !Number(it.has_label_photos)) return false;

            if (!term) return true;

            const hay = [
                it.id,
                it.nome_prodotto,
                it.fornitore,
                it.lotto,
                it.trattamento,
                it.stato,
                it.data_abbattimento,
                it.source_mode,
                it.has_label_photos ? "foto etichette" : "",
                it.cryo_mac_address,
                it.cryo_device_id ? "cryo abbattimenti cuore" : "",
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return hay.includes(term);
        });
    }, [items, q, onlyInCorso, onlyScaduti, onlyConFoto]);

    return (
        <Page title="Registro Abbattimenti" back>
            <div className="max-w-5xl mx-auto p-6">
                <div className="bg-white rounded-2xl shadow border border-slate-200 p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                        <div className="flex-1 relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                className="w-full border rounded-xl pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                placeholder="Cerca per prodotto, lotto, fornitore…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                        </div>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={onlyInCorso}
                                onChange={(e) => setOnlyInCorso(e.target.checked)}
                            />
                            Solo in corso
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={onlyScaduti}
                                onChange={(e) => setOnlyScaduti(e.target.checked)}
                            />
                            Solo scaduti
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={onlyConFoto}
                                onChange={(e) => setOnlyConFoto(e.target.checked)}
                            />
                            Solo con foto
                        </label>

                        <button
                            type="button"
                            onClick={load}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Aggiorna
                        </button>
                    </div>

                    {err && <div className="mt-3 text-sm text-red-600">{err}</div>}

                    <div className="mt-2 text-xs text-slate-500">
                        Totale: <b>{filtered.length}</b>
                    </div>
                </div>

                <div className="mt-4 space-y-3">
                    {loading && <div className="text-sm text-slate-500">Caricamento…</div>}

                    {!loading && filtered.length === 0 && (
                        <div className="bg-white rounded-2xl shadow border border-slate-200 p-5 text-slate-600">
                            Nessuna registrazione trovata.
                        </div>
                    )}

                    {filtered.map((it) => {
                        const stato = it.stato || "—";
                        const isOverdue = !!it.is_overdue;
                        const hasPhotos = Number(it.has_label_photos) > 0;
                        const photosCount = Number(it.label_photos_count || 0);
                        const isCompleting = completingId === it.id;
                        const isSaving = savingId === it.id;

                        const docsUrl = it.public_token
                            ? `/api/abbattimenti_docs.php?t=${encodeURIComponent(it.public_token)}`
                            : null;

                        const labelUrl = `/api/abbattimenti_pdf.php?id=${encodeURIComponent(it.id)}`;

                        return (
                            <div
                                key={it.id}
                                className="bg-white rounded-2xl shadow border border-slate-200 p-4"
                            >
                                <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="text-lg font-bold truncate">
                                                {it.nome_prodotto || "—"}
                                            </div>

                                            <span
                                                className={`text-xs border px-2 py-1 rounded-full ${clsBadge(
                                                    stato,
                                                    isOverdue
                                                )}`}
                                            >
                                                {stato === "completato"
                                                    ? "Completato"
                                                    : isOverdue
                                                        ? "Scaduto"
                                                        : "In corso"}
                                            </span>

                                            {it.trattamento && (
                                                <span className="text-xs border px-2 py-1 rounded-full bg-slate-50 text-slate-700 border-slate-200">
                                                    {it.trattamento}
                                                </span>
                                            )}

                                            {it.source_mode && (
                                                <span className="text-xs border px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border-indigo-200">
                                                    {sourceModeLabel(it.source_mode)}
                                                </span>
                                            )}

                                            {hasPhotos && (
                                                <span className="inline-flex items-center gap-1 text-xs border px-2 py-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200">
                                                    <ImageIcon className="w-3.5 h-3.5" />
                                                    Foto etichette: {photosCount}
                                                </span>
                                            )}

                                            {it.cryo_device_id && (
                                                <span className="inline-flex items-center gap-1 text-xs border px-2 py-1 rounded-full bg-cyan-50 text-cyan-700 border-cyan-200">
                                                    <Thermometer className="w-3.5 h-3.5" />
                                                    Cryo a cuore
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-slate-700">
                                            <div>
                                                <span className="text-slate-500">ID:</span>{" "}
                                                <span className="font-mono">{it.id}</span>
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Data:</span>{" "}
                                                {fmtDate(it.data_abbattimento)}
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Lotto:</span>{" "}
                                                <span className="font-mono">{it.lotto || "—"}</span>
                                            </div>
                                            <div className="truncate">
                                                <span className="text-slate-500">Fornitore:</span>{" "}
                                                {it.fornitore || "—"}
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Inizio:</span>{" "}
                                                {fmtDT(it.inizio)}
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Scadenza:</span>{" "}
                                                {fmtDT(it.deadline)}
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Temp. inizio:</span>{" "}
                                                {it.temp_inizio ?? "—"}°C
                                            </div>
                                            <div>
                                                <span className="text-slate-500">Temp. fine:</span>{" "}
                                                {it.temp_fine ?? "—"}°C
                                            </div>
                                            {it.cryo_device_id && (
                                                <div className="md:col-span-2">
                                                    <span className="text-slate-500">Cryo:</span>{" "}
                                                    <span className="font-mono">{it.cryo_mac_address || `#${it.cryo_device_id}`}</span>
                                                    {it.cryo_monitoring_enabled ? " · monitoraggio attivo" : ""}
                                                    <SnapshotMini snapshot={it.cryo_snapshot_start} />
                                                    <SnapshotMini snapshot={it.cryo_snapshot_end} />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex md:flex-col gap-2 md:items-end">
                                        {docsUrl && (
                                            <a
                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-slate-900 text-white hover:bg-slate-800"
                                                href={docsUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Apri documenti collegati"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                Documenti
                                            </a>
                                        )}

                                        <a
                                            className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-blue-600 text-white hover:bg-blue-700"
                                            href={labelUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            title="Stampa etichetta"
                                        >
                                            <Printer className="w-4 h-4" />
                                            Etichetta
                                        </a>

                                        {stato !== "completato" && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setCompletingId((prev) => (prev === it.id ? null : it.id))
                                                }
                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700"
                                            >
                                                <CheckCircle2 className="w-4 h-4" />
                                                Completa
                                            </button>
                                        )}

                                        {stato !== "completato" && it.cryo_device_id && it.cryo_monitoring_enabled ? (
                                            <button
                                                type="button"
                                                onClick={() => captureCryoLive(it)}
                                                disabled={capturingId === it.id}
                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-cyan-700 text-white hover:bg-cyan-800 disabled:opacity-50"
                                            >
                                                <Thermometer className="w-4 h-4" />
                                                Cryo live
                                            </button>
                                        ) : null}

                                        <div className="text-xs text-slate-500 flex items-center gap-1">
                                            {stato === "completato" ? (
                                                <>
                                                    <CheckCircle2 className="w-4 h-4" /> ok
                                                </>
                                            ) : isOverdue ? (
                                                <>
                                                    <AlertTriangle className="w-4 h-4" /> fuori tempo
                                                </>
                                            ) : (
                                                <>
                                                    <Clock className="w-4 h-4" /> in corso
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {it.cryo_device_id && stato !== "completato" && (
                                    <CryoTrendCard
                                        snapshot={cryoLiveById[it.id] || it.cryo_snapshot_start}
                                        rows={cryoTrendById[it.id] || []}
                                    />
                                )}

                                {stato !== "completato" && isCompleting && (
                                    <div className="mt-4 border-t border-slate-200 pt-4">
                                        <div className="flex flex-col md:flex-row md:items-end gap-3">
                                            <div className="flex-1">
                                                <label className="block text-xs font-semibold text-slate-500 mb-1">
                                                    Temperatura finale
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={tempFineById[it.id] ?? ""}
                                                    onChange={(e) =>
                                                        setTempFineById((prev) => ({
                                                            ...prev,
                                                            [it.id]: e.target.value,
                                                        }))
                                                    }
                                                    className="w-full border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                                    placeholder="Es. -18.0"
                                                />
                                                {it.cryo_device_id && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => captureCryoEnd(it)}
                                                            disabled={capturingId === it.id || isSaving}
                                                            className="mt-2 inline-flex items-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800 hover:bg-cyan-100 disabled:opacity-50"
                                                        >
                                                            <Thermometer className="w-4 h-4" />
                                                            {capturingId === it.id ? "Lettura..." : "Cattura fine da Cryo"}
                                                        </button>
                                                        <SnapshotMini snapshot={cryoEndById[it.id]} />
                                                    </>
                                                )}
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleComplete(it)}
                                                    disabled={isSaving}
                                                    className="px-4 py-2 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                                >
                                                    {isSaving ? "Salvataggio..." : "Conferma completamento"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCompletingId(null)}
                                                    disabled={isSaving}
                                                    className="px-4 py-2 rounded-2xl border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                                >
                                                    Annulla
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </Page>
    );
}
